import { formatDistance } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useSnapshot } from 'valtio';

import deleteMedia from '@cartwhl/common/api/deleteMedia';
import putMediaName from '@cartwhl/common/api/putMediaName';
import { ExpandedMediaRecordFromAPI, FromStore } from '@cartwhl/common/types';
import { getStudioUrl } from '@cartwhl/common/utils';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Input from '@cartwhl/ui/atoms/Input';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import SortIcon from '@cartwhl/ui/icons/SortIcon';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import Button from '@cartwhl/ui/input/Button';
import EmptyState from '@cartwhl/ui/messaging/EmptyState';
import AnimationPreview from '@cartwhl/ui/molecules/AnimationPreview';
import Modal from '@cartwhl/ui/molecules/Modal';

import { galleryStore, noticesStore, userStore } from 'stores/global';

import ProfileSection from './ProfileSection';
import ProfileTabLoadingState from './ProfileTabLoadingState';

type SortBy = 'Newest' | 'Oldest';

export default function UploadedMedia() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading: userLoading } = useSnapshot(userStore.state);
  const { mediaRecords } = useSnapshot(galleryStore.state);
  const [sorted, setSorted] = useState<ExpandedMediaRecordFromAPI[]>([]);
  const [sortBy, setSortBy] = useState<SortBy>('Newest');

  const records = useMemo(
    () => (mediaRecords.data?.items || []) as ExpandedMediaRecordFromAPI[],
    [mediaRecords.data?.items],
  );

  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [selectedMedia, setSelectedMedia] =
    useState<FromStore<ExpandedMediaRecordFromAPI> | null>(null);

  useEffect(() => {
    if (records.length === 0) return;
    // Get mediaId param from url
    const mediaId = searchParams.get('mediaId');
    const media = records.find((record) => record.mediaID === mediaId);
    if (media) setSelectedMedia(media);
  }, [records, searchParams]);

  useEffect(() => {
    if (records.length === 0) return;
    // Set url param
    if (selectedMedia) {
      setSearchParams({ mediaId: selectedMedia.mediaID });
    } else {
      setSearchParams({});
    }
  }, [selectedMedia, records.length, setSearchParams]);

  useEffect(() => {
    if (records.length > 0) {
      const list = Array.prototype.slice.call(records, 0);
      if (sortBy === 'Newest') {
        setSorted(list);
      } else {
        setSorted([...list].reverse());
      }
    }
  }, [records, sortBy]);

  useEffect(() => {
    if (records.length === 0 && mediaRecords.status === 'initial') {
      galleryStore.fetchMediaRecords();
    }
  }, [records.length, mediaRecords.status]);

  if (userLoading || mediaRecords.status === 'pending') {
    return <ProfileTabLoadingState />;
  }

  return (
    <div className="overview max-w-(--breakpoint-md) mx-auto">
      <ProfileSection
        title="Uploaded videos"
        boxed={false}
        className="mt-8"
        actions={
          <div className="flex gap-2">
            <Dropdown trigger={<Button variant="tertiary" icon={<SortIcon />} />}>
              <Text
                as="div"
                variant="web/body/md"
                className="px-4 py-2 opacity-30 dark:opacity-50"
              >
                Sort by
              </Text>
              <Dropdown.Item onClick={() => setSortBy('Newest')}>
                <div>
                  <CheckmarkIcon
                    className={`mr-2 inline ${
                      sortBy === 'Newest' ? 'opacity-100' : 'opacity-0'
                    }`}
                  />{' '}
                  Newest
                </div>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSortBy('Oldest')}>
                <div>
                  <CheckmarkIcon
                    className={`mr-2 inline ${
                      sortBy === 'Oldest' ? 'opacity-100' : 'opacity-0'
                    }`}
                  />{' '}
                  Oldest
                </div>
              </Dropdown.Item>
            </Dropdown>
          </div>
        }
      >

        {selectedMedia && (
          <div className="mb-4 flex flex-nowrap gap-2">
            <Button
              variant="tertiary"
              icon={<ChevronLeftIcon />}
              onClick={() => setSelectedMedia(null)}
            >
              Motion from {selectedMedia.mediaName}
            </Button>
            <Dropdown
              trigger={
                <Button
                  variant="tertiary"
                  icon={<ThreeDotsIcon />}
                  className="ml-auto"
                />
              }
            >
              <Dropdown.Item onClick={() => setShowRenameModal(true)}>
                Rename
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setShowDeleteModal(true)}>
                <span className="text-red">Delete</span>
              </Dropdown.Item>
            </Dropdown>
            {showRenameModal && (
              <RenameModal
                mediaID={selectedMedia.mediaID}
                mediaName={selectedMedia.mediaName}
                close={() => setShowRenameModal(false)}
                onRename={() => {
                  galleryStore.fetchMediaRecords();
                  setShowRenameModal(false);
                }}
              />
            )}
            {showDeleteModal && (
              <DeleteModal
                mediaID={selectedMedia.mediaID}
                close={() => setShowDeleteModal(false)}
                onDelete={() => {
                  setSelectedMedia(null);
                  setShowDeleteModal(false);
                  galleryStore.fetchMediaRecords();
                }}
              />
            )}
          </div>
        )}

        {mediaRecords.status === 'ready' && records.length === 0 ? (
          <EmptyState description="You have no videos" />
        ) : (
          <>
            {/* if nothing selected show overview of all videos */}
            {selectedMedia === null ? (
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 lg:gap-6">
                {sorted.map((record) => (
                  <AnimationPreview
                    key={record.mediaID}
                    title={record.mediaName}
                    aspect="video"
                    subtitle={
                      formatDistance(record.createdAt, new Date(), {
                        includeSeconds: false,
                        addSuffix: true,
                      }).replace('about', '') +
                      ` • ${record.usedBy.length} generation${
                        record.usedBy.length > 1 ? 's' : ''
                      }`
                    }
                    mediaPath={record.mediaDownloadURL}
                    mediaType="video"
                    onClick={() => setSelectedMedia(record)}
                  />
                ))}
              </div>
            ) : (
              // if media is selected show detailed view
              <div>
                <video
                  src={selectedMedia.mediaDownloadURL}
                  controls
                  playsInline
                  muted
                  loop
                  className="mb-6 max-h-[384px] w-full rounded-lg object-contain"
                />
                <Text as="p" variant="web/body/medium/md">
                  Used in {selectedMedia.expandedUsedBy.length} animation
                  {selectedMedia.expandedUsedBy.length > 1 ? 's' : ''}
                </Text>
                <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3 lg:gap-6">
                  {selectedMedia.expandedUsedBy.map((usage) => (
                    <AnimationPreview
                      key={usage.jobID}
                      title={usage.sessionName}
                      aspect="video"
                      subtitle={formatDistance(usage.createdAt, new Date(), {
                        includeSeconds: false,
                        addSuffix: true,
                      })}
                      mediaPath={usage.mp4Path}
                      mediaType="video"
                      onClick={() => {
                        window.location.href = `${getStudioUrl()}/home?jobID=${
                          usage.jobID
                        }&animationNumber=${usage.animationNumber}`;
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </ProfileSection>
    </div>
  );
}

const RenameModal = ({
  close,
  onRename,
  mediaID,
  mediaName,
}: {
  close: () => void;
  onRename: () => void;
  mediaID: string;
  mediaName: string;
}) => {
  const [newName, setNewName] = useState(mediaName);
  const [loading, setLoading] = useState(false);

  const submitNameChange = async () => {
    setLoading(true);
    if (newName.length > 100) {
      noticesStore.error(
        `Name must be less than 100 characters. ${newName.length} characters entered.`,
      );
      return;
    }
    if (newName.length === 0) {
      noticesStore.error('Name must not be empty.');
      return;
    }
    await putMediaName(mediaID, newName);
    setLoading(false);

    onRename();
  };

  return (
    <Modal open title="Rename Media">
      <div>
        <Input
          autoFocus={newName === mediaName}
          name="text"
          label="Media Name"
          value={newName}
          className="mb-8 mt-4"
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Updated media name"
        />
      </div>
      <div className="mt-6 flex justify-between">
        <Button onClick={close} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" loading={loading} onClick={submitNameChange}>
          Rename
        </Button>
      </div>
    </Modal>
  );
};

const DeleteModal = ({
  close,
  onDelete,
  mediaID,
}: {
  close: () => void;
  onDelete: () => void;
  mediaID: string;
}) => {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);

    await deleteMedia(mediaID);

    onDelete();
  };
  return (
    <Modal
      open
      title="Delete video?"
      description="Are you sure you want to delete this video? Only the video will be deleted from your profile. All associated motions generated from this video will not be affected or removed. You will still be able to view those motions in your History tab."
    >
      <div className="mt-6 flex justify-between">
        <Button onClick={close} disabled={loading}>
          Close
        </Button>

        <Button
          loading={loading}
          variant="secondary-destructive"
          onClick={handleDelete}
        >
          Delete video
        </Button>
      </div>
    </Modal>
  );
};
